import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

import useLogout from '@/application/auth/logout';
import { useUserStore } from '@/stores/user/userStore';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';


import useTeamManagementStore from '@/stores/teamManagement/teamManagementStore';
import { useTimezoneStore } from '@/stores/timezone/timezoneStore';
import * as teamManagementService from '@/services/teamManagement.service';
import DUserAvatar from '@/components/Global/DUserAvatar';
import UserIcon from '@/shared/icons/NewUserIcon';
import MembershipIcon from '@/shared/icons/MembershipIcon';
import DDarkModeSelector from '@/components/DDarkModeSelector';
import LogoutIcon from '@/shared/icons/LogoutIcon';
import IconWrapper from '@/shared/components/UI/IconWrapper';
import DSpinner from '@/components/Global/DSpinner';
import mixpanel from '@/utils/mixpanel';

const HeaderAccountMenu = ({ closeMobileNav}) => {
  const logout = useLogout();
  const user = useUserStore((state) => state.user);
  const selectedTeam = useTeamManagementStore((state) => state.selectedTeam);
  const timezone = useTimezoneStore((state) => state.timezone);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [isLoadingRole, setIsLoadingRole] = useState(false);

  // Fetch current user's role in the selected team
  useEffect(() => {
    const fetchCurrentUserRole = async () => {
      if (!selectedTeam || !user?.id) {
        setCurrentUserRole(null);
        setIsLoadingRole(false);
        return;
      }

      setIsLoadingRole(true);
      try {
        const membersResponse = await teamManagementService.getTeamMembers(selectedTeam.id, timezone);
        if (membersResponse.status === 200) {
          const members = membersResponse.data.results || [];
          const currentUserMember = members.find(member => member.user_id === user.id);
          if (currentUserMember && currentUserMember.team_role) {
            setCurrentUserRole(currentUserMember.team_role.name);
          } else {
            setCurrentUserRole(null);
          }
        }
      } catch (error) {
        console.error('Error fetching current user role:', error);
        setCurrentUserRole(null);
      } finally {
        setIsLoadingRole(false);
      }
    };

    fetchCurrentUserRole();
  }, [selectedTeam, user?.id, timezone]);

  // Determine what to display: role in team, spinner while loading, or 'Admin' if no team selected
  const displayContent = selectedTeam ? (
    isLoadingRole ? (
      <div className="min-w-[45px] flex items-center justify-center">
        <DSpinner style={{ height: 16, width: 16 }} />
      </div>
    ) : (
      currentUserRole
    )
  ) : (
    'Admin'
  );

  return (
    <div className="text-right h-full flex flex-col justify-center items-center">
      <Menu>
        <MenuButton className="flex items-center justify-center gap-2 rounded-full bg-v3-card-border font-semibold text-v3-black-max focus:outline-none data-[focus]:outline-1 data-[focus]:outline-black py-[2px] pr-[2px] pl-[12px] hover:bg-v3-menu-hover">
          <div className='font-medium text-v3-h10 text-v3-black-heading flex items-center justify-center'>
            {typeof displayContent === 'string' ? <p>{displayContent}</p> : displayContent}
          </div>
          <DUserAvatar size="sm" image={user?.profile_image} />
        </MenuButton>

        <MenuItems
          transition
          anchor="bottom end"
          className="w-[323px] mt-[11px] origin-top-right rounded-v3-sm border border-v3-card-border shadow-v3-card bg-white   text-v3-black-max transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 z-20"
        >
          <MenuItem>
            <div className="p-v3-sm flex flex-col gap-v3-xxs">
              <p className="text-v3-h9 text-v3-black-max font-medium">{user?.full_name}</p>
              <p className="text-v3-h11 text-v3-black-secondary">{user?.email}</p>
            </div>
          </MenuItem>
          <MenuItem>
            <Link
              className="group flex text-v3-black-max items-center gap-v3-xs py-[12px] px-v3-sm text-v3-h9 font-medium  tracking-tight hover:text-v3-purple-primary rounded-v3-xxs"
              to="/profile"
              onClick={closeMobileNav}
            >
               <IconWrapper size={20}>
                <UserIcon className="text-v3-black-max  w-full h-full group-hover:text-v3-purple-primary" />
              </IconWrapper>
              <span>Profile</span>
            </Link>
          </MenuItem>
          {!selectedTeam && <MenuItem>
            <Link
              className="group flex text-v3-black-max items-center gap-v3-xs py-[12px] px-v3-sm text-v3-h9 font-medium  tracking-tight hover:text-v3-purple-primary rounded-v3-xxs"
              to="/membership"
              onClick={closeMobileNav}
            >
               <IconWrapper size={20}>
                <MembershipIcon className="text-v3-black-max w-full h-full group-hover:text-v3-purple-primary" />
              </IconWrapper>
              <span>Membership</span>
            </Link>
          </MenuItem>}
          {/* Branding menu item removed - Issue #1531 */}
          <MenuItem>
            <DDarkModeSelector />
          </MenuItem>
          <MenuItem>
            <button
              className="group dbutton flex items-center gap-v3-xs py-[12px] px-v3-sm text-v3-h9 font-medium  text-v3-black-max tracking-tight hover:text-v3-purple-primary rounded-v3-xxs w-full"
              onClick={() => { mixpanel.track('Logout'); logout(); }}
            >
               <IconWrapper size={20}>
                <LogoutIcon className="text-v3-black-max  w-full h-full group-hover:text-v3-purple-primary" />
              </IconWrapper>
              <span>Logout</span>
            </button>
          </MenuItem>
        </MenuItems>
      </Menu>
    </div>
  );
};
export default HeaderAccountMenu;

